import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Checkbox from '@amzn/meridian/checkbox';
import Image from '@amzn/meridian/image';
import Row from '@amzn/meridian/row';
import type { SelectProps } from '@amzn/meridian/select/select';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { ALLOWED_VIDEO_DURATIONS, BENDER_ASPECT_RATIOS, LIVE_STATUSES } from 'src/constants';
import { type Template } from 'src/types/template.types';
import type { FormRhfControllerProps } from 'src/components/common/forms/types';
import { TemplatesDropdown } from 'src/components/campaigns/forms/TemplateSelect';
import { TimezoneSelect } from 'src/components/common/forms/TimezoneSelect';
import { sanitizeMeridianImageSrc } from 'src/utils';

export interface TemplateSelectProps
  extends Omit<
  SelectProps,
  'children' | 'name' | 'value' | 'onBlur' | 'onChange'
  >,
  FormRhfControllerProps {
  countryCode?: string;
  index: number;
  isThumbnailTemplate?: boolean;
  templates: Template[];
  testId: string;
  destinationLinkConfigName?: string;
  isFirstMultiTitleTemplate?: boolean;
  useForAll?: boolean;
  onUseForAllChange?: (value: boolean) => void;
}

export const ImageTemplatePreview: React.FC<{
  index: number;
  isThumbnailTemplate?: boolean;
  previewImageUrl: string;
}> = ({ index, isThumbnailTemplate, previewImageUrl }) => {
  const { t } = useTranslation();

  return (
    <>
      <Text type="b200">{t('template_preview')}</Text>
      <div style={{ maxWidth: '200px' }}>
        <Image
          data-testid={`template-preview-image-${isThumbnailTemplate ? 'thumbnail-' : ''}${index}`}
          size="contain"
          src={sanitizeMeridianImageSrc(previewImageUrl)}
          viewportAspectRatio={1}
        />
      </div>
    </>
  );
};

export const VideoTemplateThumbnail: React.FC<{ aspectRatio: string; testId?: string }> = ({ aspectRatio, testId }) => {
  const { t } = useTranslation();
  const width = 60;
  const height = aspectRatio === BENDER_ASPECT_RATIOS._9X16 ? width / 9 * 16 : width;

  return (
    <Box
      backgroundColor="#F6F7F8"
      data-testid={testId}
      height={`${height}px`}
      type="fill"
      width={`${width}px`}
    >
      <Column alignmentHorizontal="center" alignmentVertical="center" height="100%">
        <Text alignment="center" color="secondary" type="b100">{t('video').toUpperCase()} {aspectRatio}</Text>
      </Column>
    </Box>
  );
};

export const VideoTemplateMetadata: React.FC<{ index: number; template: Template }> = ({ index, template }) => {
  const { t } = useTranslation();

  return (
  <>
    <Text type="b200">{t('template_metadata')}</Text>
    <Text data-testid={`template-metadata-${index}`} type="b100">{template.assetType}, {template.aspectRatio}, {t('min_width')} {template.minWidth}</Text>
  </>
  );
};

interface ConfigurationProps extends Pick<TemplateSelectProps, 'control' | 'index' | 'name' | 'testId'> {
  thumbnailTemplateOptions?: Template[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  videoDurations?: any;
  hasTimezone?: boolean;
  countryCode?: string;
}

export const Configuration: React.FC<ConfigurationProps> = ({
  control,
  index,
  name,
  testId,
  thumbnailTemplateOptions,
  videoDurations,
  hasTimezone,
  countryCode,
}) => {
  const { t } = useTranslation();

  const videoDurationOptions = useMemo<string[] | undefined>(() => {
    const templateDurations = videoDurations?.items?.enum as string[];
    if (templateDurations) {
      return templateDurations.filter(duration => ALLOWED_VIDEO_DURATIONS.has(duration));
    }
  }, [videoDurations?.items?.enum]);

  return (
    <>
      {videoDurationOptions && videoDurationOptions.length > 0 && (
        <VideoDurationsConfig control={control} name={`${name}.config.videoDurations`} videoDurationOptions={videoDurationOptions} />
      )}
      {hasTimezone && countryCode && (
        <TimezoneSelect control={control} countryCode={countryCode} name={name} />
      )}
      {thumbnailTemplateOptions && (
        <TemplatesDropdown
          control={control}
          index={index}
          isThumbnailTemplate
          label={t('form_bender_thumbnail_template_label')}
          name={`${name}.config.thumbnailTemplateId`}
          templates={thumbnailTemplateOptions}
          testId={`${testId}-thumbnail-template-select`}
        />
      )}
    </>
  );
};

interface CheckboxGroupConfigProps extends Pick<TemplateSelectProps, 'control' | 'name'> {
  label: string;
  options: readonly string[];
  testId: string;
  formatLabel?: (value: string) => string;
}

const CheckboxGroupConfig: React.FC<CheckboxGroupConfigProps> = ({
  control,
  name,
  label,
  options,
  testId,
  formatLabel,
}) => {
  const { setValue } = useFormContext();
  const value = useWatch({ control, name }) as string[] | undefined;

  return (
    <Column data-testid={testId} spacing="100">
      <Text type="b200">{label}</Text>
      <Row spacing="400">
        {options.map((option) => (
          <Checkbox
            checked={value?.includes(option)}
            data-testid={`${testId}-option-${option}`}
            key={option}
            name={name}
            value={option}
            onChange={(checked: boolean) => {
              const selected = value ? [...value] : [];
              if (checked) {
                selected.push(option);
              } else {
                selected.splice(selected.indexOf(option), 1);
              }
              // Keep the stored array in canonical option order, independent of click order.
              selected.sort((a, b) => options.indexOf(a) - options.indexOf(b));
              setValue(name, selected, { shouldDirty: true });
            }}
          >
            {formatLabel ? formatLabel(option) : option}
          </Checkbox>
        ))}
      </Row>
    </Column>
  );
};

interface VideoDurationsConfigProps extends Pick<TemplateSelectProps, 'control' | 'name'> {
  videoDurationOptions: string[];
}

export const VideoDurationsConfig: React.FC<VideoDurationsConfigProps> = ({
  control,
  name,
  videoDurationOptions,
}) => {
  const { t } = useTranslation();
  return (
    <CheckboxGroupConfig
      control={control}
      formatLabel={(v) => `${v}${t('seconds_shorthand')}`}
      label={t('template_video_duration')}
      name={name}
      options={videoDurationOptions}
      testId="config-video-durations"
    />
  );
};

interface LiveStatusesConfigProps extends Pick<TemplateSelectProps, 'control' | 'name'> {}

export const LiveStatusesConfig: React.FC<LiveStatusesConfigProps> = ({
  control,
  name,
}) => {
  const { t } = useTranslation();
  return (
    <CheckboxGroupConfig
      control={control}
      formatLabel={(v) => v.replace(/_/g, ' ')}
      label={t('form_live_statuses')}
      name={name}
      options={LIVE_STATUSES}
      testId="config-live-statuses"
    />
  );
};
